/**
 * MaterialsTab — Iter276a Phase 2 (rev. 2026-06-27)
 *
 * Component-grouped Materials Overview. Mirrors the per-component
 * card layout the operator uses on the Contractors tab so they can
 * walk through Aquaseal → Sheet → Ridge → … one card at a time and
 * verify each Type Name has the right supplier / ratio / unit /
 * material quantity before generating the per-supplier order PDFs
 * in Iter276b.
 *
 * Driven by:
 *   GET /api/appraisals/{id}/materials-overview?component_type=...
 *
 * The backend groups its response by SUPPLIER (Roofing Industries,
 * Allfast, …). This view flips the grouping client-side to
 * COMPONENT_TYPE → TYPE_NAME (the catalogue row) — same shape as
 * `ContractorOverviewTab.componentSections`.
 *
 * No edits / overrides yet — strictly read-only walkthrough.
 */
import { FixingsCard } from '@/components/materials/FixingsCard';
import FlashingSiteCheck from '@/components/FlashingSiteCheck';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Boxes, RefreshCw, AlertTriangle, FileDown, Settings, ChevronRight, Ruler } from 'lucide-react';
import { toast } from 'sonner';

const COMPONENT_TYPE_LABELS = { Aquaseal: 'Dektite' };
const displayCt = (t) => COMPONENT_TYPE_LABELS[t] || t;

// Slug used by the catalogue-audit URL (matches `/catalogue-audit/<slug>`).
const CATALOGUE_AUDIT_SLUG = {
  Aquaseal: 'aquaseal', Sheet: 'sheet', Ridge: 'ridge', Barge: 'barge',
  Valley: 'valley', Flashing: 'flashing', Spouting: 'spouting',
  Tile: 'tile', Underlay: 'underlay',
};

function fmtMoney(v) {
  const n = Number(v || 0);
  return `$${n.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtNum(v, d = 2) {
  const n = Number(v || 0);
  return n.toLocaleString('en-NZ', { maximumFractionDigits: d });
}

export default function MaterialsTab({ api, appraisalId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(''); // '' = All components
  // Iter276-length — Stick-rule admin modal state.
  const [stickRuleOpen, setStickRuleOpen] = useState(false);
  // Iter276b — Materials Order PDF modal state.
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  const load = useCallback(async (componentType = '') => {
    if (!appraisalId) return;
    setLoading(true);
    try {
      const qs = componentType ? `?component_type=${encodeURIComponent(componentType)}` : '';
      const { data: r } = await api.get(`/appraisals/${appraisalId}/materials-overview${qs}`);
      setData(r);
    } catch (err) {
      toast.error('Failed to load materials overview');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId]);

  // Iter276a-rev5 — inline edit of `materials_desc` / `supplier_code`
  // on the matching `component_rate_tables` row. Immediate save on
  // blur (no per-cell confirm), then reload the overview to refresh
  // every row that resolves to the same catalogue row.
  const saveCatalogueField = useCallback(async (catalogueRowId, field, value) => {
    if (!catalogueRowId || !['materials_desc', 'supplier_code'].includes(field)) return;
    try {
      await api.put(`/component-rate-tables/${catalogueRowId}`, { [field]: value });
      toast.success(`Updated ${field === 'materials_desc' ? 'Supplier name' : 'Code'}`);
      await load(filter);
    } catch (err) {
      toast.error(`Save failed: ${err?.response?.data?.detail || err.message}`);
    }
  }, [api, filter, load]);

  useEffect(() => { load(filter); }, [load, filter]);

  // Flip backend's supplier-grouped payload into per-component cards.
  // Iter276a-rev3 — operator wants ONE ROW PER SOURCE REF (no grouping
  // across refs that happen to share a catalogue Type Name + supplier
  // code). Sorted by Ref with natural ordering (F2 before F11).
  const componentSections = useMemo(() => {
    if (!data) return [];
    const sections = {};   // ct -> { ct, rows:[], unresolved:[] }
    const getSection = (ct) => {
      if (!sections[ct]) sections[ct] = { component_type: ct, rows: [], unresolved: [] };
      return sections[ct];
    };
    // Walk suppliers → materials → source_lines and emit one flat row per source_line.
    (data.suppliers || []).forEach((sup) => {
      (sup.materials || []).forEach((mat) => {
        (mat.source_lines || []).forEach((sl) => {
          const ct = sl.component_type || 'Unknown';
          const sec = getSection(ct);
          const material_qty = Number(sl.material_qty || 0);
          const materials_rate = Number(mat.materials_rate || 0);
          sec.rows.push({
            ref:            sl.ref || '',
            type_name:      sl.name || '—',
            profile:        sl.profile || '',
            qty:            Number(sl.qty || 0),
            length:         Number(sl.length || 0),
            supplier:       sup.supplier,
            supplier_code:  mat.supplier_code || '',
            materials_desc: mat.materials_desc || '',
            ratio:          Number(mat.ratio || 1),
            unit:           mat.unit || '',
            material_qty,
            materials_rate,
            est_total:      material_qty * materials_rate,
            catalogue_row_id: mat.catalogue_row_id || '',
            // Iter276-length — pieces / stock-each / total-stock are
            // populated for length-priced rows (Ridge / Valley /
            // Barge / Flashing / Spouting). Zero on non-length rows.
            pieces:         Number(sl.pieces || 0),
            stock_each_m:   Number(sl.stock_each_m || 0),
            total_stock_m:  Number(sl.total_stock_m || 0),
            raw_length_m:   Number(sl.raw_length_m || 0),
            stick_rule_applied: !!sl.stick_rule_applied,
            // Iter276-recipe — flagged when the parent catalogue
            // line was fanned out into multiple supplier rows via
            // a `material_recipes` document.
            from_recipe:    !!sl.from_recipe,
            flashing_spec:  sl.flashing_spec || '',
            chosen_colour:  sl.chosen_colour || '',
            has_folds:      (sl.folds || []).length > 0,
          });
        });
      });
    });
    // Lines with a catalogue match but no supplier (visual sibling of unresolved)
    (data.needs_supplier || []).forEach((sl) => {
      const ct = sl.component_type || 'Unknown';
      getSection(ct).unresolved.push({
        ...sl, reason: 'No supplier on catalogue row', kind: 'needs_supplier',
      });
    });
    (data.unresolved || []).forEach((sl) => {
      const ct = sl.component_type || 'Unknown';
      getSection(ct).unresolved.push({ ...sl, kind: 'unresolved' });
    });
    // Natural Ref sort: split into alpha-prefix + numeric-tail so
    //   F2 < F11, Sp1 < Sp10, B5 < B40, M1 < M10.
    const refKey = (s) => {
      const m = String(s || '').match(/^\s*([A-Za-z]*)(\d*)/);
      return [(m && m[1]) || '', Number((m && m[2]) || 0)];
    };
    const cmpRef = (a, b) => {
      const [aa, an] = refKey(a);
      const [ba, bn] = refKey(b);
      return aa.localeCompare(ba, undefined, { sensitivity: 'base' }) || (an - bn);
    };
    return Object.values(sections).map((sec) => {
      const rows = sec.rows.slice().sort((a, b) => cmpRef(a.ref, b.ref));
      const unresolved = sec.unresolved.slice().sort((a, b) => cmpRef(a.ref, b.ref));
      const section_lines = rows.length;
      const section_materials = new Set(rows.map((r) => `${r.supplier_code}|${r.materials_desc}`)).size;
      const section_est_total = rows.reduce((s, r) => s + r.est_total, 0);
      const suppliers = new Set(rows.map((r) => r.supplier).filter(Boolean));
      // Iter276-length — section is "stick-ruled" when at least one row
      // had the calc applied. Drives Pieces / Stock-len column visibility
      // per card so Dektite/Misc stay clean.
      const section_stick_rule = rows.some((r) => r.stick_rule_applied);
      const section_pieces = rows.reduce((s, r) => s + (r.pieces || 0), 0);
      const section_total_stock = rows.reduce((s, r) => s + (r.total_stock_m || 0), 0);
      return {
        component_type: sec.component_type,
        type_rows: rows,
        suppliers,
        section_lines,
        section_materials,
        section_est_total,
        section_stick_rule,
        section_pieces,
        section_total_stock,
        unresolved,
      };
    }).sort((a, b) => a.component_type.localeCompare(b.component_type));
  }, [data]);

  // Top-strip aggregates
  const totals = useMemo(() => {
    let components = componentSections.length;
    let materials = 0, lines = 0, value = 0;
    componentSections.forEach((s) => {
      materials += s.section_materials;
      lines     += s.section_lines;
      value     += s.section_est_total;
    });
    return { components, materials, lines, value };
  }, [componentSections]);

  return (
    <div className="space-y-5" data-testid="materials-tab">
      <FixingsCard fixings={data?.fixings} />
      <FlashingSiteCheck api={api} appraisalId={appraisalId} />
      {/* Action bar — mirrors ContractorOverviewTab */}
      <div className="flex items-center justify-between flex-wrap gap-3 border-b border-slate-200 pb-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Boxes size={20} className="text-orange-600" />
            Materials Overview
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Walk through each component, verify the catalogue match + supplier ratio before generating order sheets.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[11px] uppercase tracking-wider text-slate-500" htmlFor="materials-component-filter">
            Component
          </label>
          <select
            id="materials-component-filter"
            data-testid="materials-component-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white"
          >
            <option value="">All components</option>
            {(data?.available_component_types || []).map((ct) => (
              <option key={ct} value={ct}>{displayCt(ct)}</option>
            ))}
          </select>
          <button
            type="button"
            data-testid="materials-stick-rule-btn"
            onClick={() => setStickRuleOpen(true)}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
            title={`Stick rule: max ${data?.stick_rule?.max_stick_m ?? '—'} m · ${((data?.stick_rule?.lap_m ?? 0) * 1000).toFixed(0)} mm lap (click to edit)`}
          >
            <Ruler size={12} />
            Stick rule
            {data?.stick_rule && (
              <span className="text-slate-500 ml-1 tabular-nums">
                {data.stick_rule.max_stick_m.toFixed(1)}m · {(data.stick_rule.lap_m * 1000).toFixed(0)}mm
              </span>
            )}
          </button>
          <button
            type="button"
            data-testid="materials-refresh-btn"
            onClick={() => load(filter)}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            data-testid="materials-orders-pdf-btn"
            onClick={() => setPdfModalOpen(true)}
            disabled={(data?.suppliers || []).length === 0}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100 disabled:opacity-40 disabled:cursor-not-allowed"
            title={(data?.suppliers || []).length === 0 ? 'No resolved suppliers yet — verify catalogue matches first.' : 'Download per-supplier Materials Order PDFs'}
          >
            <FileDown size={12} /> Materials Order PDFs
          </button>
        </div>
      </div>

      {/* Grand-totals strip (mirrors Contractors tab Stat row) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="materials-summary">
        <Stat label="Components" value={totals.components} testid="materials-summary-components" />
        <Stat label="Materials" value={totals.materials} testid="materials-summary-materials" />
        <Stat label="Source lines" value={totals.lines} testid="materials-summary-lines" />
        <Stat label="Est. materials value" value={fmtMoney(totals.value)} accent="orange" testid="materials-summary-value" />
      </div>

      {/* Empty / loading */}
      {loading && !data && (
        <div className="py-16 text-center text-slate-400" data-testid="materials-loading">Loading materials overview…</div>
      )}
      {data && !loading && componentSections.length === 0 && (
        <div className="rounded-md border border-dashed border-slate-300 p-12 text-center text-slate-400" data-testid="materials-empty">
          No quote items yet for {filter ? `${displayCt(filter)} on this appraisal` : 'this appraisal'}.
        </div>
      )}

      {/* Per-component cards (alphabetical) */}
      <div className="grid grid-cols-1 gap-4" data-testid="materials-work-list">
        {componentSections.map((sec) => (
          <ComponentCard key={sec.component_type} sec={sec} onSaveField={saveCatalogueField} />
        ))}
      </div>

      {stickRuleOpen && (
        <StickRuleModal
          api={api}
          current={data?.stick_rule}
          onClose={() => setStickRuleOpen(false)}
          onSaved={() => { setStickRuleOpen(false); load(filter); }}
        />
      )}

      {pdfModalOpen && (
        <MaterialsOrderPdfModal
          api={api}
          appraisalId={appraisalId}
          suppliers={data?.suppliers || []}
          componentType={filter}
          onClose={() => setPdfModalOpen(false)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const accentClass = accent === 'orange'
    ? 'text-orange-700'
    : 'text-slate-900';
  return (
    <div data-testid={testid} className="border border-slate-200 rounded-lg bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${accentClass}`}>{value}</div>
    </div>
  );
}

/**
 * StickRuleModal — Iter276-length
 *
 * Lightweight admin modal to edit the office's stick rule (max stick
 * length + lap allowance). Reads + writes
 * `/api/admin/stick-rule-defaults`. Only admin users can save (the
 * endpoint enforces `require_admin`); the modal surfaces a friendly
 * error if a non-admin tries.
 *
 * The rule values drive:
 *  • RoofDiagramAnnotator pin calc
 *  • AppraisalComponentsTab roll-up
 *  • Materials Overview tab (per source-line enrichment)
 *  • Materials Order list (per supplier sticks)
 *
 * Default values 8.0 m / 200 mm. Operators were running 9.0 m on the
 * prior supplier; we changed to 8.0 m on 2026-06-28.
 */
function StickRuleModal({ api, current, onClose, onSaved }) {
  const [maxM, setMaxM] = useState(current?.max_stick_m ?? 8.0);
  const [lapMm, setLapMm] = useState(((current?.lap_m ?? 0.2) * 1000));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/stick-rule-defaults', {
        max_stick_m: Number(maxM),
        lap_m: Number(lapMm) / 1000,
      });
      toast.success('Stick rule updated — refreshing materials');
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Save failed${detail ? `: ${detail}` : ''}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
      data-testid="stick-rule-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 w-full max-w-md">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Ruler size={16} className="text-orange-600" />
            Stick rule (pieces with lap)
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xs"
            data-testid="stick-rule-modal-close"
          >Close ✕</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            Sets the maximum stick length the supplier&apos;s mill will roll, plus the lap allowance
            added per piece when a length has to be divided. Used by Ridge / Valley / Barge /
            Flashing / Spouting calcs (annotator pins, Components tab, Materials Overview).
            Single-piece sticks carry no lap.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Max stick length (m)</span>
              <input
                type="number"
                step="0.1"
                min="1"
                max="30"
                value={maxM}
                onChange={(e) => setMaxM(e.target.value)}
                data-testid="stick-rule-max-input"
                className="w-full border border-slate-300 rounded-md px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Lap allowance (mm)</span>
              <input
                type="number"
                step="10"
                min="0"
                max="1000"
                value={lapMm}
                onChange={(e) => setLapMm(e.target.value)}
                data-testid="stick-rule-lap-input"
                className="w-full border border-slate-300 rounded-md px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
            </label>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
            <b>Heads-up:</b> changing these values immediately re-runs every length-priced
            calc across the system (annotator pins, Components tab, Materials Order list).
            Existing PDFs / saved quotes aren&apos;t recalculated until regenerated.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50/40">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-100"
            data-testid="stick-rule-cancel-btn"
          >Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="stick-rule-save-btn"
            className="text-xs px-3 py-1.5 rounded-md bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
          >{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * EditableCell — Iter276a-rev5
 *
 * Click-to-edit table cell with immediate save on blur (Excel-style).
 * The cell renders as a plain text span until clicked; on click it
 * swaps to an input, focuses it, and on blur (or Enter) calls
 * `onSave(newValue)` only if the value actually changed. Escape
 * cancels without saving. `disabled` hides the affordance so rows
 * that haven't resolved to a catalogue row (unresolved buckets)
 * stay read-only.
 */
function EditableCell({ value, placeholder, className = '', disabled = false, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  // Keep draft in sync if the parent reloads after a save
  useEffect(() => { if (!editing) setDraft(value || ''); }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next === (value || '').trim()) return;
    onSave?.(next);
  };
  const cancel = () => { setEditing(false); setDraft(value || ''); };

  if (disabled) {
    return (
      <td className={`px-2 py-1.5 ${className}`} data-testid={testid}>
        {value || placeholder || ''}
      </td>
    );
  }
  return (
    <td className={`px-2 py-1.5 group ${className}`} data-testid={testid}>
      {editing ? (
        <input
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          className="w-full border border-orange-300 rounded px-1.5 py-0.5 text-xs bg-white outline-none focus:ring-1 focus:ring-orange-400"
          data-testid={`${testid}-input`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-left w-full hover:bg-orange-50 rounded px-1 -mx-1 transition-colors"
          title="Click to edit (saves on blur)"
        >
          {value || <span className="text-slate-400 italic">{placeholder || 'edit…'}</span>}
        </button>
      )}
    </td>
  );
}

function ComponentCard({ sec, onSaveField }) {
  const slug = CATALOGUE_AUDIT_SLUG[sec.component_type] || sec.component_type.toLowerCase();
  const supplierList = Array.from(sec.suppliers).sort().join(', ') || '—';
  // Hide Length column when no row in this section has a non-zero length
  // (e.g. Dektite items are unit-qty only). Operator-friendly conditional
  // column visibility — keeps Flashing / Barge / Spouting / etc. unchanged.
  const showLength = sec.type_rows.some((r) => Number(r.length) > 0);
  // Iter276a-rev5 — hide Ratio when every row in this section uses
  // ratio === 1 (Dektite is 1:1, no ratio meaningful).
  const showRatio = sec.type_rows.some((r) => Number(r.ratio) !== 1);
  // Iter276-length — Pieces / Stock-len columns surface only on cards
  // where the stick-rule actually fired (length-priced components with
  // a positive measured length).
  const showSticks = !!sec.section_stick_rule;
  return (
    <div
      className="border border-slate-200 rounded-lg overflow-hidden"
      data-testid={`materials-section-${sec.component_type}`}
    >
      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-slate-700">{displayCt(sec.component_type)}</span>
          <a
            href={`/catalogue-audit/${slug}`}
            data-testid={`materials-section-audit-link-${sec.component_type}`}
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50"
            title={`Open the ${displayCt(sec.component_type)} catalogue audit (same window — use ← Back to return)`}
          >
            <Settings size={10} /> Edit catalogue <ChevronRight size={11} />
          </a>
        </div>
        <span className="text-[11px] text-slate-500">
          {sec.section_lines} line{sec.section_lines === 1 ? '' : 's'}
          <span className="mx-2 text-slate-300">·</span>
          {sec.section_materials} material{sec.section_materials === 1 ? '' : 's'}
          <span className="mx-2 text-slate-300">·</span>
          Suppliers <b className="text-slate-700">{supplierList}</b>
          <span className="mx-2 text-slate-300">·</span>
          Est. <b className="text-orange-700 tabular-nums">{fmtMoney(sec.section_est_total)}</b>
          {sec.unresolved.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold border border-amber-300">
              {sec.unresolved.length} unresolved
            </span>
          )}
        </span>
      </div>

      {/* Materials table */}
      {sec.type_rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ minWidth: 1180 }}>
            <thead>
              <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-orange-50/50">
                <th className="text-left px-2 py-1.5">Ref</th>
                <th className="text-left px-2 py-1.5">Type / Catalogue name</th>
                <th className="text-left px-2 py-1.5">Profile</th>
                <th className="text-right px-2 py-1.5" title="Sum of quote-line quantities">Qty</th>
                {showLength && (
                  <th className="text-right px-2 py-1.5" title="Sum of quote-line lengths (m)">Length</th>
                )}
                {showSticks && (
                  <>
                    <th className="text-right px-2 py-1.5" title="Pieces with lap — calculated from the configured stick rule (Max length / Lap). Single-piece sticks carry no lap.">Pieces</th>
                    <th className="text-right px-2 py-1.5" title="Stock length per piece — per_piece + lap (only when pieces > 1)">Stock len</th>
                  </>
                )}
                <th className="text-left px-2 py-1.5">Supplier</th>
                <th className="text-left px-2 py-1.5" title="Supplier's product code (from the catalogue row) — click to edit">Code</th>
                <th className="text-left px-2 py-1.5" title="Supplier's product name (materials_desc on the catalogue row) — click to edit">Supplier name</th>
                {showRatio && (
                  <th className="text-right px-2 py-1.5" title="Materials per quote unit">Ratio</th>
                )}
                <th className="text-left px-2 py-1.5">Unit</th>
                <th className="text-right px-2 py-1.5" title="Material quantity to order (base × ratio)">Mat qty</th>
                <th className="text-right px-2 py-1.5">Rate</th>
                <th className="text-right px-2 py-1.5 bg-orange-50/60">Est. $</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sec.type_rows.map((row, i) => (
                <tr
                  key={`${row.ref}-${row.supplier_code}-${i}`}
                  className="hover:bg-slate-50"
                  data-testid={`materials-row-${sec.component_type}-${row.ref || i}`}
                >
                  <td className="px-2 py-1.5 text-[11px] text-slate-500 font-mono">{row.ref || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-800">
                    {row.type_name}
                    {row.from_recipe && (
                      <span
                        className="ml-2 inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200"
                        title="Expanded from a material recipe — one parent quote line fanned into multiple supplier rows"
                        data-testid={`materials-recipe-badge-${sec.component_type}-${row.ref || ''}`}
                      >Recipe</span>
                    )}
                    {row.flashing_spec && (
                      <div className="text-[10px] text-slate-500 leading-snug mt-0.5" data-testid={`materials-flashing-spec-${row.ref || i}`}>{row.flashing_spec}</div>
                    )}
                    {sec.component_type === 'Flashing' && !row.has_folds && (
                      <div className="text-[10px] font-semibold text-red-700 mt-0.5" data-testid={`materials-flashing-spec-missing-${row.ref || i}`}>No template / folds — supplier can't fold this</div>
                    )}
                    {!row.flashing_spec && row.chosen_colour && (
                      <div className="text-[10px] text-slate-500 mt-0.5">Colour {row.chosen_colour}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{row.profile || '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtNum(row.qty)}</td>
                  {showLength && (
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtNum(row.length)}</td>
                  )}
                  {showSticks && (
                    <>
                      <td className="px-2 py-1.5 text-right tabular-nums">{row.pieces > 0 ? fmtNum(row.pieces, 0) : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{row.stock_each_m > 0 ? fmtNum(row.stock_each_m, 3) : '—'}</td>
                    </>
                  )}
                  <td className="px-2 py-1.5">{row.supplier || '—'}</td>
                  <EditableCell
                    value={row.supplier_code}
                    placeholder="—"
                    className="font-mono text-[11px] text-slate-700"
                    disabled={!row.catalogue_row_id}
                    testid={`materials-edit-code-${sec.component_type}-${row.ref || i}`}
                    onSave={(v) => onSaveField(row.catalogue_row_id, 'supplier_code', v)}
                  />
                  <EditableCell
                    value={row.materials_desc}
                    placeholder="—"
                    className="text-slate-700"
                    disabled={!row.catalogue_row_id}
                    testid={`materials-edit-desc-${sec.component_type}-${row.ref || i}`}
                    onSave={(v) => onSaveField(row.catalogue_row_id, 'materials_desc', v)}
                  />
                  {showRatio && (
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtNum(row.ratio, 3)}</td>
                  )}
                  <td className="px-2 py-1.5">{row.unit || '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-900">{fmtNum(row.material_qty, 3)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney(row.materials_rate)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-orange-50/40">{fmtMoney(row.est_total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50/70 border-t border-slate-200 text-[11px]">
                <td className="px-2 py-1.5 text-slate-500 uppercase tracking-wider font-bold" colSpan={4 + (showLength ? 1 : 0) + (showSticks ? 2 : 0)}>
                  {displayCt(sec.component_type)} subtotal
                </td>
                <td className="px-2 py-1.5 text-slate-500" colSpan={6 + (showRatio ? 1 : 0)}>
                  {sec.section_materials} material{sec.section_materials === 1 ? '' : 's'} · {sec.section_lines} line{sec.section_lines === 1 ? '' : 's'}
                  {showSticks && (
                    <>
                      {' · '}
                      <b className="text-slate-700 tabular-nums">{sec.section_pieces}</b>
                      {' pieces · '}
                      <b className="text-slate-700 tabular-nums">{fmtNum(sec.section_total_stock, 2)} m</b>
                      {' total stock'}
                    </>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right font-bold tabular-nums text-orange-800 bg-orange-50/40">
                  {fmtMoney(sec.section_est_total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}

      {/* Per-card unresolved strip */}
      {sec.unresolved.length > 0 && (
        <div className="border-t border-amber-200 bg-amber-50/40 px-4 py-2.5" data-testid={`materials-section-unresolved-${sec.component_type}`}>
          <div className="flex items-center gap-2 text-amber-900 mb-1.5">
            <AlertTriangle size={13} />
            <span className="text-[11px] font-bold uppercase tracking-wider">
              {sec.unresolved.length} unresolved · awaiting catalogue work
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-amber-900/80 uppercase tracking-wider">
                  <th className="text-left px-2 py-1">Ref</th>
                  <th className="text-left px-2 py-1">Quote item</th>
                  <th className="text-left px-2 py-1">Profile</th>
                  <th className="text-right px-2 py-1">Qty</th>
                  <th className="text-right px-2 py-1">Length</th>
                  <th className="text-left px-2 py-1">Reason</th>
                </tr>
              </thead>
              <tbody>
                {sec.unresolved.map((row, i) => (
                  <tr key={i} className="border-t border-amber-200/60">
                    <td className="px-2 py-1 font-mono text-[11px]">{row.ref || '—'}</td>
                    <td className="px-2 py-1">{row.name || '—'}</td>
                    <td className="px-2 py-1">{row.profile || '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtNum(row.qty)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtNum(row.length)}</td>
                    <td className="px-2 py-1 text-[11px] text-amber-900">{row.reason || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * MaterialsOrderPdfModal — Iter276b
 * Datepicker + site-notes textarea + per-supplier download list.
 * Downloads the PDF blob via fetch → object URL → anchor click, so
 * the browser respects the filename returned by the backend and
 * shows a proper "Save as" dialog.
 */
function MaterialsOrderPdfModal({ api, appraisalId, suppliers, componentType, onClose }) {
  const [deliveryDate, setDeliveryDate] = useState('');
  const [siteNotes, setSiteNotes] = useState('');
  const [downloading, setDownloading] = useState(null); // supplier slug or 'zip'

  const doDownload = async (kind, supplierName) => {
    setDownloading(kind === 'zip' ? 'zip' : supplierName);
    try {
      const params = new URLSearchParams();
      if (componentType) params.set('component_type', componentType);
      if (deliveryDate) params.set('delivery_date', deliveryDate);
      if (siteNotes)    params.set('site_notes', siteNotes);
      const isZip = kind === 'zip';
      if (!isZip) params.set('supplier', supplierName);
      const path = isZip
        ? `/appraisals/${appraisalId}/materials-order-pdfs.zip?${params}`
        : `/appraisals/${appraisalId}/materials-order-pdf?${params}`;
      const resp = await api.get(path, { responseType: 'blob' });
      const disp = resp.headers?.['content-disposition'] || '';
      const m = /filename="?([^"]+?)"?(?:;|$)/.exec(disp);
      const filename = m ? m[1] : (isZip ? 'materials-orders.zip' : `materials-order-${supplierName}.pdf`);
      const blob = new Blob([resp.data], { type: isZip ? 'application/zip' : 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(isZip ? 'ZIP downloaded' : `${supplierName} PDF downloaded`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Download failed: ${detail}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="materials-order-pdf-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <FileDown size={14} className="text-orange-600" />
            Download Materials Order PDFs
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg leading-none">&times;</button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1" htmlFor="pdf-delivery-date">
              Delivery date
            </label>
            <input
              id="pdf-delivery-date"
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              data-testid="materials-order-pdf-delivery-date"
              className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1" htmlFor="pdf-site-notes">
              Site notes for driver <span className="text-slate-400 normal-case font-normal">(optional)</span>
            </label>
            <textarea
              id="pdf-site-notes"
              value={siteNotes}
              onChange={(e) => setSiteNotes(e.target.value)}
              data-testid="materials-order-pdf-site-notes"
              placeholder="e.g. Deliver to the rear driveway. Sign at kitchen door."
              rows={2}
              className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>

          <div className="pt-1 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-bold">Download</div>
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={() => doDownload('zip')}
                disabled={downloading !== null || suppliers.length === 0}
                data-testid="materials-order-pdf-download-all"
                className="w-full inline-flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-md border border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100 disabled:opacity-50"
              >
                <span className="font-bold flex items-center gap-2">
                  <FileDown size={12} /> All suppliers (ZIP)
                </span>
                <span className="text-[10px] font-normal text-slate-500">{suppliers.length} PDF{suppliers.length === 1 ? '' : 's'}</span>
              </button>
              {suppliers.map((sup) => (
                <button
                  key={sup.supplier}
                  type="button"
                  onClick={() => doDownload('single', sup.supplier)}
                  disabled={downloading !== null}
                  data-testid={`materials-order-pdf-download-${sup.supplier}`}
                  className="w-full inline-flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
                >
                  <span className="font-medium text-slate-800">{sup.supplier}</span>
                  <span className="text-[10px] font-normal text-slate-500">
                    {sup.materials_count} material{sup.materials_count === 1 ? '' : 's'} · {sup.line_count} source line{sup.line_count === 1 ? '' : 's'}
                  </span>
                </button>
              ))}
              {suppliers.length === 0 && (
                <div className="text-xs text-slate-500 italic text-center py-3">
                  No resolved suppliers. Verify catalogue matches first.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 py-2 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50" data-testid="materials-order-pdf-modal-close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

